import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import type {
  AudienceMatchType,
  AudienceRule,
  MemberAttributeGroupCatalog
} from '../../../../utils/memberAttributes';

// Shown wherever an audience is chosen, so Any/All reads the same in the
// Elliott chat and on the Members step.
export const AUDIENCE_MATCH_EXPLAINER =
  'Any: has at least one of the values you pick. All: has every one of them. If you use more than one list, people must match each list.';

export const AudienceMatchToggle: React.FC<{
  value: AudienceMatchType;
  // A match type with no values picked has no meaning, so callers disable it.
  disabled?: boolean;
  onChange: (next: AudienceMatchType) => void;
}> = ({ value, disabled = false, onChange }) => (
  <Box
    role="radiogroup"
    aria-label="Match any or all"
    sx={{
      display: 'inline-flex',
      flexShrink: 0,
      border: '1px solid #2A2A2A',
      borderRadius: '4px',
      overflow: 'hidden',
      opacity: disabled ? 0.4 : 1
    }}
  >
    {(['any', 'all'] as AudienceMatchType[]).map((mode) => {
      const active = value === mode;
      return (
        <Box
          key={mode}
          component="button"
          type="button"
          role="radio"
          aria-checked={active}
          disabled={disabled}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (!disabled) onChange(mode);
          }}
          sx={{
            px: '8px',
            py: '2px',
            border: 'none',
            bgcolor: active ? '#F79420' : 'transparent',
            color: active ? '#FFFFFF' : '#868686',
            fontFamily: 'Inter, sans-serif',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'background-color 0.12s',
            '&:hover': disabled ? {} : { bgcolor: active ? '#F79420' : '#262626' }
          }}
        >
          {mode}
        </Box>
      );
    })}
  </Box>
);

const ValuePill: React.FC<{
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, selected, disabled = false, onClick }) => (
  <Box
    component="button"
    type="button"
    aria-pressed={selected}
    disabled={disabled}
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      px: '10px',
      py: '5px',
      borderRadius: '16px',
      border: `1px solid ${selected ? '#F79420' : '#2A2A2A'}`,
      bgcolor: selected ? 'rgba(247, 148, 32, 0.12)' : '#1C1C1C',
      color: selected ? '#FDFDFD' : '#B6B6B6',
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      cursor: disabled ? 'default' : 'pointer',
      transition: 'border-color 0.12s, background-color 0.12s',
      '&:hover': disabled ? {} : { borderColor: '#F79420' }
    }}
  >
    {selected && <CheckIcon sx={{ fontSize: 14, color: '#F79420' }} />}
    {label}
  </Box>
);

interface Props {
  catalog: MemberAttributeGroupCatalog[];
  rules: AudienceRule[];
  onChange: (next: AudienceRule[]) => void;
  allLearners: boolean;
  onAllLearnersChange: (next: boolean) => void;
  disabled?: boolean;
}

/**
 * Inline audience editor for the creation-flow Members step: every org
 * attribute list with all its values (select or deselect any of them), an
 * Any/All toggle per list, and "All learners". New values can't be created
 * here — that stays in Organization Settings.
 */
const AudienceRulesEditor: React.FC<Props> = ({
  catalog,
  rules,
  onChange,
  allLearners,
  onAllLearnersChange,
  disabled = false
}) => {
  const ruleFor = (list: MemberAttributeGroupCatalog): AudienceRule =>
    rules.find((r) => r.listId === list._id) ?? {
      listId: list._id,
      listName: list.name,
      matchType: 'any',
      valueIds: []
    };

  // Replaces the list's rule. An emptied list keeps its entry so its Any/All
  // survives re-selecting; callers strip valueless rules before saving.
  const replaceRule = (list: MemberAttributeGroupCatalog, rule: AudienceRule) =>
    onChange([...rules.filter((r) => r.listId !== list._id), { ...rule, listName: list.name }]);

  const toggleValue = (list: MemberAttributeGroupCatalog, valueId: string) => {
    const rule = ruleFor(list);
    const valueIds = rule.valueIds.includes(valueId)
      ? rule.valueIds.filter((v) => v !== valueId)
      : [...rule.valueIds, valueId];
    replaceRule(list, { ...rule, valueIds });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ color: '#868686', fontSize: 12, fontFamily: 'Inter, sans-serif', lineHeight: '18px' }}>
        {AUDIENCE_MATCH_EXPLAINER}
      </Typography>

      <Box>
        <ValuePill
          label="All learners in the organization"
          selected={allLearners}
          disabled={disabled}
          onClick={() => onAllLearnersChange(!allLearners)}
        />
      </Box>

      {catalog.length === 0 ? (
        <Typography sx={{ color: '#868686', fontSize: 13, fontFamily: 'Inter, sans-serif' }}>
          No attributes are configured for this organization.
        </Typography>
      ) : (
        catalog.map((list) => {
          const rule = ruleFor(list);
          // All learners overrides the lists, so they sit dimmed and inert.
          const listDisabled = disabled || allLearners;
          return (
            <Box
              key={list._id}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: allLearners ? 0.45 : 1 }}
            >
              {/* Any/All sits right beside the list title rather than at the far edge. */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.25 }}>
                <Typography
                  sx={{
                    color: '#868686',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {list.name}
                </Typography>
                <AudienceMatchToggle
                  value={rule.matchType}
                  disabled={listDisabled || rule.valueIds.length === 0}
                  onChange={(matchType) => replaceRule(list, { ...rule, matchType })}
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {list.values.map((value) => (
                  <ValuePill
                    key={value._id}
                    label={value.value}
                    selected={rule.valueIds.includes(value._id)}
                    disabled={listDisabled}
                    onClick={() => toggleValue(list, value._id)}
                  />
                ))}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default AudienceRulesEditor;
